import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHandlerService, UserDTO, convertToFormData, ActivityIndicatorType, interceptorsToken, LoginResponseDTO, MART_USER_KEY, MART_TOKEN_KEY, StorageService } from 'library-explorer';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { CartService } from './cart.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  public authenticatedUser$ = new BehaviorSubject<UserDTO | false>(false);
  private userToken$ = new BehaviorSubject<string | null>(null);
  user: UserDTO;
  otpEmail: string;

  constructor(private httpWrapper: HttpHandlerService, private storageService: StorageService, private cartService: CartService) { }

  get authUser(): Observable<UserDTO | false> {
    return this.authenticatedUser$.asObservable();
  }

  get authUserValue(): UserDTO | false {
    return this.authenticatedUser$.value;
  }

  updateAuthUser(user: UserDTO): void {
    this.user = user;
    this.storageService.setStorageValue(MART_USER_KEY, JSON.stringify(user));
    this.authenticatedUser$.next(user);
  }

  get userTokenValue(): string | null {
    return this.userToken$.value;
  }

  updateUserToken(token: string): void {
    this.storageService.setStorageValue(MART_TOKEN_KEY, token);
    this.userToken$.next(token);
  }

  async init() {
    const authUserToken = await this.storageService.check4Value(MART_TOKEN_KEY);
    const authUser = await this.storageService.check4Value(MART_USER_KEY);

    if (!authUser || !authUserToken) return;

    this.authenticateUser({token: authUserToken, user: JSON.parse(authUser)} as LoginResponseDTO);
  }

  login(formDataJson: any): Observable<UserDTO> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<LoginResponseDTO>('login', formData, undefined, headers)
      .pipe(
        tap((res: LoginResponseDTO) => this.authenticateUser(res)),
        map((res: LoginResponseDTO) => res.user)
      );
  }

  register(formDataJson: any): Observable<UserDTO> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<UserDTO>('register', formData, undefined, headers).pipe(
      tap((user: UserDTO) => {
        this.user = user;
      })
    );
  }

  resetPassword(formDataJson: any): Observable<UserDTO> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<UserDTO>('forgot-password', formData, undefined, headers);
  }

  requestOtp(formDataJson: any): Observable<void> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<void>('send-otp', formData, undefined, headers);
  }

  changePassword(formDataJson: any): Observable<UserDTO> {
    return this.httpWrapper.putRequest<UserDTO>(`change-password`, formDataJson, undefined,
      new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen));
  }

  deleteMyAccount(): Observable<UserDTO> {
    return this.httpWrapper.deleteRequest<UserDTO>(`users/delete-account`, undefined,
      new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen));
  }

  verifyUserEmail(formDataJson: any): Observable<UserDTO> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<LoginResponseDTO>('verify-user', formData, undefined, headers)
      .pipe(
        tap((res: LoginResponseDTO) => this.authenticateUser(res)),
        map((res: LoginResponseDTO) => res.user)
      );
  }

  registerDistributor(formDataJson: any): Observable<any> {
    const formData = convertToFormData(formDataJson);
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);

    return this.httpWrapper.postRequest<any>('distributor', formData, undefined, headers);
  }

  private authenticateUser(loginResponse: LoginResponseDTO) {
    this.updateUserToken(loginResponse.token);
    this.updateAuthUser(loginResponse.user);

    this.cartService.init();
  }

  logout() {
    this.storageService.removeStorageValue(MART_USER_KEY);
    this.storageService.removeStorageValue(MART_TOKEN_KEY);
    this.userToken$.next(null);
    this.cartService.emptyCart()
    this.authenticatedUser$.next(false)
  }

  refreshCsrfToken() {
    return this.httpWrapper.getThirdPartyRequest(`/api/sanctum/csrf-cookie`,
      { withCredentials: true }
    );
  }
}
